import { Component, Input, OnInit } from '@angular/core'
import {
  IssueStatusName,
  issueStatusNameToDisplayName,
} from '@helios/ag-grid-config'
import { take } from 'rxjs/operators'
import { BreakHistory } from '../../model/break.model'
import { MultiBreaksService } from '../../services/multi-breaks.service'

const DEFAULT_VISIBLE = 2

@Component({
  selector: 'hs-break-history',
  templateUrl: './break-history.component.html',
  styleUrls: ['./break-history.component.scss'],
})
export class BreakHistoryComponent implements OnInit {
  @Input() breakId = ''

  history: BreakHistory[] = []
  isLoading = true
  hasError = false
  expanded = false

  constructor(private readonly breaksService: MultiBreaksService) {}

  ngOnInit(): void {
    this.breaksService
      .getBreakHistory(this.breakId)
      .pipe(take(1))
      .subscribe({
        next: history => {
          this.history = [...history].sort(
            (a, b) =>
              new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
          )
          this.isLoading = false
        },
        error: () => {
          this.isLoading = false
          this.hasError = true
        },
      })
  }

  get visibleHistory(): BreakHistory[] {
    return this.expanded ? this.history : this.history.slice(0, DEFAULT_VISIBLE)
  }

  get hiddenCount(): number {
    return Math.max(0, this.history.length - DEFAULT_VISIBLE)
  }

  get hasMore(): boolean {
    return this.history.length > DEFAULT_VISIBLE
  }

  statusLabel(status: IssueStatusName): string {
    return issueStatusNameToDisplayName(status)
  }

  /** CSS modifier (added via ngClass) for dot + badge color, keyed on status. */
  statusClass(status: IssueStatusName): string {
    switch (status) {
      case IssueStatusName.FOUND:
        return 'active'
      case IssueStatusName.WAITING:
        return 'held'
      case IssueStatusName.IGNORED:
        return 'accepted'
      default:
        return 'unknown'
    }
  }

  /** Trim surrounding whitespace; tolerate null/undefined (codes arrive untrimmed). */
  code(value?: string): string {
    return (value ?? '').trim()
  }

  /** True when an entry has any detail worth showing in the card */
  hasDetail(entry: BreakHistory): boolean {
    return !!(
      this.code(entry.actionCode) ||
      this.code(entry.descriptionCode) ||
      this.code(entry.note) ||
      this.code(entry.researchNote)
    )
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded
  }
}
